import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  EditalResponseDTO,
  AvaliadorDashboardDTO,
  PendentesResponse,
  InscricaoIsencaoResponseDTO,
  InscricaoIsencaoDetalhadaResponseDTO,
  InscricaoRecursoResponseDTO,
  InscricaoRecursoDetalhadaResponseDTO,
  InscricaoCotaResponseDTO,
  InscricaoCotaDetalhadaResponseDTO,
  InscricaoCondicaoEspecialResponseDTO,
  InscricaoCondicaoEspecialDetalhadaResponseDTO,
  AvaliacaoRequestDTO,
  AvaliacaoIsencaoHistoricoDTO,
  AvaliacaoRecursoHistoricoDTO,
  AvaliacaoCotaCondicaoDTO,
  CandidatoBasicoDTO,
  DocumentoDownloadDTO,
  TipoRecursoResponseDTO,
  TipoIsencaoResponseDTO,
  TipoCotaResponseDTO,
  TipoCondicaoEspecialResponseDTO,
  AvaliadorDashboardGeralDTO,
} from '../models/avaliador-avaliacao.model';

@Injectable({
  providedIn: 'root'
})
export class AvaliadorAvaliacaoService {
  private apiUrl = `${environment.apiUrl}/avaliador`;
  private apiAvaliadoresUrl = `${environment.apiUrl}/avaliadores`;

  constructor(private http: HttpClient) {}

  // ============================================
  // 1. Informações do Avaliador e Editais
  // ============================================

  /**
   * Lista todos os editais aos quais o avaliador autenticado foi designado
   * GET /api/avaliadores/meus-editais
   */
  listarMeusEditais(): Observable<EditalResponseDTO[]> {
    return this.http.get<EditalResponseDTO[]>(`${this.apiAvaliadoresUrl}/meus-editais`);
  }

  /**
   * Retorna detalhes completos de um edital específico
   * GET /api/avaliadores/meus-editais/{editalId}
   */
  buscarDetalhesEdital(editalId: number): Observable<EditalResponseDTO> {
    return this.http.get<EditalResponseDTO>(`${this.apiAvaliadoresUrl}/meus-editais/${editalId}`);
  }

  /**
   * Dashboard do Avaliador (por Edital)
   * GET /api/avaliador/dashboard?editalId={editalId}
   */
  getDashboard(editalId: number): Observable<AvaliadorDashboardDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<AvaliadorDashboardDTO>(`${this.apiUrl}/avaliacoes/dashboard`, { params });
  }

  /**
   * Dashboard geral do avaliador
   * GET /api/avaliador/avaliacoes/dashboard-geral
   */
  getDashboardGeral(): Observable<AvaliadorDashboardGeralDTO> {
    return this.http.get<AvaliadorDashboardGeralDTO>(`${this.apiUrl}/avaliacoes/dashboard-geral`);
  }

  // ============================================
  // 2. Visualização de Solicitações Pendentes
  // ============================================

  /**
   * Lista todas as solicitações pendentes (Cotas + Condições Especiais)
   * GET /api/avaliador/avaliacoes/pendentes?editalId={editalId}
   */
  listarPendentes(editalId: number): Observable<PendentesResponse> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<PendentesResponse>(`${this.apiUrl}/avaliacoes/pendentes`, { params });
  }

  /**
   * Lista isenções pendentes
   * GET /api/avaliador/avaliacoes/isencoes/pendentes?editalId={editalId}
   */
  listarIsencoesPendentes(editalId: number): Observable<InscricaoIsencaoResponseDTO[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<InscricaoIsencaoResponseDTO[]>(`${this.apiUrl}/avaliacoes/isencoes/pendentes`, { params });
  }

  /**
   * Lista recursos pendentes
   * GET /api/avaliador/avaliacoes/recursos/pendentes?editalId={editalId}
   */
  listarRecursosPendentes(editalId: number): Observable<InscricaoRecursoResponseDTO[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<InscricaoRecursoResponseDTO[]>(`${this.apiUrl}/avaliacoes/recursos/pendentes`, { params });
  }

  /**
   * Lista cotas pendentes
   * GET /api/avaliador/avaliacoes/cotas/pendentes?editalId={editalId}
   */
  listarCotasPendentes(editalId: number): Observable<InscricaoCotaResponseDTO[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<InscricaoCotaResponseDTO[]>(`${this.apiUrl}/avaliacoes/cotas/pendentes`, { params });
  }

  /**
   * Lista condições especiais pendentes
   * GET /api/avaliador/avaliacoes/condicoes-especiais/pendentes?editalId={editalId}
   */
  listarCondicoesEspeciaisPendentes(editalId: number): Observable<InscricaoCondicaoEspecialResponseDTO[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<InscricaoCondicaoEspecialResponseDTO[]>(`${this.apiUrl}/avaliacoes/condicoes-especiais/pendentes`, { params });
  }

  /**
   * Lista solicitações por tipo e status
   * GET /api/avaliador/avaliacoes/solicitacoes/{tipo}/status/{status}?editalId={editalId}
   */
  listarSolicitacoesPorStatus(
    tipo: 'isencao' | 'cota' | 'condicao-especial' | 'recurso',
    status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO',
    editalId: number
  ): Observable<any[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/solicitacoes/${tipo}/status/${status}`, { params });
  }

  // ============================================
  // 3. Avaliação de Isenções
  // ============================================

  /**
   * Visualizar detalhes de uma isenção
   * GET /api/avaliador/avaliacoes/isencoes/{inscricaoIsencaoId}
   */
  visualizarDetalhesIsencao(inscricaoIsencaoId: number): Observable<InscricaoIsencaoDetalhadaResponseDTO> {
    return this.http.get<InscricaoIsencaoDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/isencoes/${inscricaoIsencaoId}`);
  }

  /**
   * Avaliar isenção (Deferir/Indeferir)
   * POST /api/avaliador/avaliacoes/isencao/{inscricaoIsencaoId}/avaliar
   */
  avaliarIsencao(inscricaoIsencaoId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/isencao/${inscricaoIsencaoId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de isenção
   * GET /api/avaliador/avaliacoes/isencoes/{inscricaoIsencaoId}/historico
   */
  visualizarHistoricoIsencao(inscricaoIsencaoId: number): Observable<AvaliacaoIsencaoHistoricoDTO[]> {
    return this.http.get<AvaliacaoIsencaoHistoricoDTO[]>(`${this.apiUrl}/avaliacoes/isencoes/${inscricaoIsencaoId}/historico`);
  }

  // ============================================
  // 4. Avaliação de Cotas
  // ============================================

  /**
   * Visualizar detalhes de uma cota
   * GET /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}
   */
  visualizarDetalhesCota(inscricaoCotaId: number): Observable<InscricaoCotaDetalhadaResponseDTO> {
    return this.http.get<InscricaoCotaDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}`);
  }

  /**
   * Avaliar cota (Deferir/Indeferir)
   * POST /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/avaliar
   */
  avaliarCota(inscricaoCotaId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de cota
   * GET /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/historico
   */
  visualizarHistoricoCota(inscricaoCotaId: number): Observable<AvaliacaoCotaCondicaoDTO[]> {
    return this.http.get<AvaliacaoCotaCondicaoDTO[]>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/historico`);
  }

  /**
   * Avaliar recurso de cota
   * POST /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/recurso/avaliar
   */
  avaliarRecursoCota(inscricaoCotaId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/recurso/avaliar`, request);
  }

  // ============================================
  // 5. Avaliação de Condições Especiais
  // ============================================

  /**
   * Visualizar detalhes de uma condição especial
   * GET /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}
   */
  visualizarDetalhesCondicaoEspecial(inscricaoCondicaoEspecialId: number): Observable<InscricaoCondicaoEspecialDetalhadaResponseDTO> {
    return this.http.get<InscricaoCondicaoEspecialDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}`);
  }

  /**
   * Avaliar condição especial (Deferir/Indeferir)
   * POST /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/avaliar
   */
  avaliarCondicaoEspecial(inscricaoCondicaoEspecialId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de condição especial
   * GET /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/historico
   */
  visualizarHistoricoCondicaoEspecial(inscricaoCondicaoEspecialId: number): Observable<AvaliacaoCotaCondicaoDTO[]> {
    return this.http.get<AvaliacaoCotaCondicaoDTO[]>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/historico`);
  }

  /**
   * Avaliar recurso de condição especial
   * POST /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/recurso/avaliar
   */
  avaliarRecursoCondicaoEspecial(inscricaoCondicaoEspecialId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/recurso/avaliar`, request);
  }

  // ============================================
  // 6. Avaliação de Recursos (InscricaoRecurso)
  // ============================================

  /**
   * Visualizar detalhes de um recurso
   * GET /api/avaliador/avaliacoes/recursos/{inscricaoRecursoId}
   */
  visualizarDetalhesRecurso(inscricaoRecursoId: number): Observable<InscricaoRecursoDetalhadaResponseDTO> {
    return this.http.get<InscricaoRecursoDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/recursos/${inscricaoRecursoId}`);
  }

  /**
   * Avaliar recurso (Responder)
   * POST /api/avaliador/avaliacoes/recurso/{inscricaoRecursoId}/avaliar
   */
  avaliarRecurso(inscricaoRecursoId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/recurso/${inscricaoRecursoId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de recursos
   * GET /api/avaliador/avaliacoes/recursos/{inscricaoRecursoId}/historico
   */
  visualizarHistoricoRecurso(inscricaoRecursoId: number): Observable<AvaliacaoRecursoHistoricoDTO[]> {
    return this.http.get<AvaliacaoRecursoHistoricoDTO[]>(`${this.apiUrl}/avaliacoes/recursos/${inscricaoRecursoId}/historico`);
  }

  // ============================================
  // 7. Endpoints Adicionais Úteis
  // ============================================

  /**
   * Buscar candidato por ID
   * GET /api/avaliador/avaliacoes/candidatos/{candidatoId}?editalId={editalId}
   */
  buscarCandidatoPorId(candidatoId: number, editalId: number): Observable<CandidatoBasicoDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<CandidatoBasicoDTO>(`${this.apiUrl}/avaliacoes/candidatos/${candidatoId}`, { params });
  }

  /**
   * Buscar candidato por CPF
   * GET /api/avaliador/avaliacoes/candidatos/cpf/{cpf}?editalId={editalId}
   */
  buscarCandidatoPorCpf(cpf: string, editalId: number): Observable<CandidatoBasicoDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<CandidatoBasicoDTO>(`${this.apiUrl}/avaliacoes/candidatos/cpf/${cpf}`, { params });
  }

  /**
   * Visualizar documento anexado
   * GET /api/avaliador/avaliacoes/documentos/{tipoSolicitacao}/{solicitacaoId}?editalId={editalId}
   */
  visualizarDocumento(
    tipoSolicitacao: 'isencao' | 'cota' | 'condicao-especial' | 'recurso',
    solicitacaoId: number,
    editalId: number
  ): Observable<DocumentoDownloadDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<DocumentoDownloadDTO>(`${this.apiUrl}/avaliacoes/documentos/${tipoSolicitacao}/${solicitacaoId}`, { params });
  }

  /**
   * Listar tipos de recurso disponíveis no edital
   * GET /api/avaliador/avaliacoes/editais/{editalId}/tipos-recursos
   */
  listarTiposRecursos(editalId: number): Observable<TipoRecursoResponseDTO[]> {
    return this.http.get<TipoRecursoResponseDTO[]>(`${this.apiUrl}/avaliacoes/editais/${editalId}/tipos-recursos`);
  }

  /**
   * Listar tipos de isenção disponíveis no edital
   * GET /api/avaliador/avaliacoes/editais/{editalId}/tipos-isencao
   */
  listarTiposIsencao(editalId: number): Observable<TipoIsencaoResponseDTO[]> {
    return this.http.get<TipoIsencaoResponseDTO[]>(`${this.apiUrl}/avaliacoes/editais/${editalId}/tipos-isencao`);
  }

  /**
   * Listar tipos de cota disponíveis no edital
   * GET /api/avaliador/avaliacoes/editais/{editalId}/tipos-cotas
   */
  listarTiposCotas(editalId: number): Observable<TipoCotaResponseDTO[]> {
    return this.http.get<TipoCotaResponseDTO[]>(`${this.apiUrl}/avaliacoes/editais/${editalId}/tipos-cotas`);
  }

  /**
   * Listar tipos de condição especial disponíveis no edital
   * GET /api/avaliador/avaliacoes/editais/{editalId}/tipos-condicoes-especiais
   */
  listarTiposCondicoesEspeciais(editalId: number): Observable<TipoCondicaoEspecialResponseDTO[]> {
    return this.http.get<TipoCondicaoEspecialResponseDTO[]>(`${this.apiUrl}/avaliacoes/editais/${editalId}/tipos-condicoes-especiais`);
  }
}
